import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Package, ShoppingCart, Users, LogOut, LayoutDashboard, Menu, X, Image as ImageIcon, BarChart3, Factory, Warehouse as WarehouseIcon, Scissors } from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'sales', 'customer'] },
    { name: 'Products', href: '/products', icon: Package, roles: ['admin', 'sales', 'customer'] },
    { name: 'Orders', href: '/orders', icon: ShoppingCart, roles: ['admin', 'sales', 'customer'] },
    { name: 'Users', href: '/users', icon: Users, roles: ['admin', 'sales'] },
    { name: 'Media', href: '/media', icon: ImageIcon, roles: ['admin', 'sales'] },
    { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['admin', 'sales'] },
    { name: 'Production', href: '/production', icon: Factory, roles: ['admin', 'production_team'] },
    { name: 'Warehouse', href: '/warehouse', icon: WarehouseIcon, roles: ['admin', 'sales', 'production_team'] },
    { name: 'Tailors', href: '/tailors', icon: Scissors, roles: ['admin'] },
  ];

  const filteredNav = navigation.filter(item => item.roles.includes(user?.role));

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14 sm:h-16">
            {/* Logo and Desktop Nav */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>JSDC</h1>
              </div>
              {/* Desktop Navigation */}
              <div className="hidden md:ml-8 md:flex md:space-x-2 lg:space-x-4">
                {filteredNav.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                      className={`inline-flex items-center px-3 lg:px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-1.5 lg:mr-2" />
                      <span className="hidden lg:inline">{item.name}</span>
                      <span className="lg:hidden">{item.name.slice(0, 4)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Desktop User Info & Logout */}
            <div className="hidden md:flex md:items-center md:space-x-3 lg:space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900 truncate max-w-[120px] lg:max-w-none">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="inline-flex items-center px-3 lg:px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
              >
                <LogOut className="w-4 h-4 lg:mr-2" />
                <span className="hidden lg:inline">Logout</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-3 space-y-1">
              {filteredNav.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={closeMobileMenu}
                    className={`flex items-center px-3 py-3 text-base font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
            <div className="border-t border-slate-200 px-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
                <button
                  onClick={() => { handleLogout(); closeMobileMenu(); }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <main className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8 safe-area-inset">
        {children}
      </main>
    </div>
  );
};
